// src/pages/RegistrarDocente.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import "../styles/formularios.css";
import "../styles/styles.css";
import Swal from "sweetalert2";
import logoInstituto from "../components/images/logo.png";
import logoSEP from "../components/images/SEP_Logo_2019.png";
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from "../utils/apiBase";

export default function RegistrarDocente() {
  const navigate = useNavigate();
  const location = useLocation();
  const handleSalir = () => {
    const rol = resolveRolBase(location.pathname);
    if (rol) {
      navigate(`/${rol}/inicio`, { replace: true });
    } else {
      // Fallback por si no se detecta rol ni en path ni en localStorage:
      navigate('/inicio', { replace: true });
    }
  };
  const apiBases = useMemo(() => resolveApiBaseList().map((base) => `${base}/docentes`), []);

  const fetchJsonWithFallback = useCallback(async (suffix, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${suffix}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const postWithFallback = useCallback(async (suffix, payload) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${suffix}`;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === "object") {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló el POST a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (trimmed && trimmed.startsWith("<")) {
          const error = new Error(`Respuesta inválida desde ${url}`);
          lastError = error;
          console.warn(`⚠️ Contenido no válido desde ${url}`);
          continue;
        }

        try {
          if (trimmed && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
            const parsed = JSON.parse(trimmed);
            const message = parsed?.message || parsed?.msg || parsed?.resultado || trimmed;
            return { text: message, raw: parsed, url };
          }
        } catch (parseErr) {
          console.warn(`⚠️ No se pudo parsear respuesta JSON desde ${url}:`, parseErr);
        }

        return { text: trimmed, url };
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al hacer POST ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // Campos
  const [idDocente, setIdDocente] = useState(""); // auto
  const [nombre, setNombre] = useState("");
  const [apellidoPaterno, setApellidoPaterno] = useState("");
  const [apellidoMaterno, setApellidoMaterno] = useState("");
  const [curp, setCurp] = useState("");
  const [telefono, setTelefono] = useState("");
  const [puesto, setPuesto] = useState("");
  const [correo, setCorreo] = useState("");
  const [sexo, setSexo] = useState(""); // "M" | "F"
  const [tipoSangre, setTipoSangre] = useState("");
  const [enviando, setEnviando] = useState(false);

  // Cargar siguiente ID al montar
  useEffect(() => {
    const cargarSiguienteIdDocente = async () => {
      try {
        const data = await fetchJsonWithFallback('/siguiente-id');
        setIdDocente(data?.siguienteId ?? "");
      } catch (err) {
        console.error("❌ Error al cargar siguiente ID:", err);
        setIdDocente("Error");
      }
    };
    cargarSiguienteIdDocente();
  }, [fetchJsonWithFallback]);

  // Validaciones iguales a tu JS
  const validar = useCallback(() => {
    if (
      !nombre.trim() ||
      !apellidoPaterno.trim() ||
      !apellidoMaterno.trim() ||
      !curp.trim() ||
      !sexo ||
      !telefono.trim() ||
      !correo.trim() ||
      !tipoSangre ||
      !puesto.trim()
    ) {
      Swal.fire("Campos incompletos", "⚠️ Todos los campos son obligatorios.", "error");
      return false;
    }
    const curpOK = /^[A-Z0-9]{18}$/.test(curp.toUpperCase());
    if (!curpOK) {
      Swal.fire("CURP inválida", "⚠️ Debe contener 18 caracteres en mayúsculas.", "error");
      return false;
    }
    const telOK = /^\d{10}$/.test(telefono);
    if (!telOK) {
      Swal.fire("Teléfono inválido", "⚠️ Debe tener 10 dígitos numéricos.", "error");
      return false;
    }
    return true;
  }, [nombre, apellidoPaterno, apellidoMaterno, curp, sexo, telefono, correo, tipoSangre, puesto]);

  const resetForm = () => {
    setNombre("");
    setApellidoPaterno("");
    setApellidoMaterno("");
    setCurp("");
    setTelefono("");
    setPuesto("");
    setCorreo("");
    setSexo("");
    setTipoSangre("");
  };

  const registrarDocente = async (e) => {
    e.preventDefault();
    if (!validar()) return;

    const payload = {
      nombre: nombre.trim(),
      apellidoPaterno: apellidoPaterno.trim(),
      apellidoMaterno: apellidoMaterno.trim(),
      curp: curp.trim().toUpperCase(),
      sexo, // el backend usa sexo[0], aquí ya es "M" o "F"
      telefono,
      correo: correo.trim(),
      tipoSangre,
      puesto: puesto.trim(),
    };

    try {
      setEnviando(true);
      const { text } = await postWithFallback('/registrar', payload);

      await Swal.fire({
        icon: "success",
        title: "Registrado",
        text: text || "✅ Docente registrado correctamente.",
        timer: 2000,
        showConfirmButton: false,
      });

      resetForm();

      // recargar el siguiente ID
      try {
        const d = await fetchJsonWithFallback('/siguiente-id');
        setIdDocente(d?.siguienteId ?? "");
      } catch {
        setIdDocente("");
      }
    } catch (err) {
      console.error("❌ Error de conexión:", err);
      Swal.fire("Error de conexión", "No fue posible conectar con el servidor.", "error");
    } finally {
      setEnviando(false);
    }
  };

  
  

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
          <img src={logoInstituto} alt="Logo Instituto" className="logo" />
          <div className="flex-grow-1 text-center">
            <h4 className="mb-0">Instituto Silviano Carrillo</h4>
            <small className="text-muted fs-5">Registrar Docente</small>
          </div>
          <img src={logoSEP} alt="Logo SEP" className="logo" />
        </div>

      {/* Formulario */}
      <div className="mb-4 p-4 rounded bg-light shadow-sm">
        <h6 className="mb-3">Registro de Docente</h6>
        <form className="row g-3" id="formDocente" onSubmit={registrarDocente}>
          {/* ID Docente */}
          <div className="col-md-2">
            <label htmlFor="idDocente" className="form-label">ID Docente</label>
            <input type="text" className="form-control" id="idDocente" value={idDocente} placeholder="Auto" disabled />
          </div>

          {/* Tipo de Usuario */}
          <div className="col-md-2">
            <label htmlFor="Usuario" className="form-label">Tipo de Usuario</label>
            <input type="text" className="form-control" id="Usuario" value="Docente" disabled />
          </div>

          {/* Nombre */}
          <div className="col-md-4">
            <label htmlFor="nombre" className="form-label">Nombre</label>
            <input
              type="text" className="form-control" id="nombre" placeholder="Nombre del docente..."
              value={nombre} onChange={(e) => setNombre(e.target.value)}
            />
          </div>

          {/* Apellido Paterno */}
          <div className="col-md-4">
            <label htmlFor="apellidoPaterno" className="form-label">Apellido Paterno</label>
            <input
              type="text" className="form-control" id="apellidoPaterno" placeholder="Apellido paterno..."
              value={apellidoPaterno} onChange={(e) => setApellidoPaterno(e.target.value)}
            />
          </div>

          {/* Apellido Materno */}
          <div className="col-md-4">
            <label htmlFor="apellidoMaterno" className="form-label">Apellido Materno</label>
            <input
              type="text" className="form-control" id="apellidoMaterno" placeholder="Apellido materno..."
              value={apellidoMaterno} onChange={(e) => setApellidoMaterno(e.target.value)}
            />
          </div>

          {/* CURP */}
          <div className="col-md-4">
            <label htmlFor="curp" className="form-label">CURP</label>
            <input
              type="text" className="form-control" id="curp" placeholder="CURP..."
              value={curp} onChange={(e) => setCurp(e.target.value.toUpperCase())} maxLength={18}
            />
          </div>

          {/* Teléfono */}
          <div className="col-md-4">
            <label htmlFor="telefono" className="form-label">Teléfono</label>
            <input
              type="tel" className="form-control" id="telefono" placeholder="Ej. 3411234567"
              value={telefono} onChange={(e) => setTelefono(e.target.value.replace(/\D/g, "").slice(0, 10))}
            />
          </div>

          {/* Puesto */}
          <div className="col-md-4">
            <label htmlFor="puesto" className="form-label">Puesto</label>
            <input
              type="text" className="form-control" id="puesto" placeholder="Puesto..."
              value={puesto} onChange={(e) => setPuesto(e.target.value)}
            />
          </div>

          {/* Correo */}
          <div className="col-md-6">
            <label htmlFor="correo" className="form-label">Correo Electrónico</label>
            <input
              type="email" className="form-control" id="correo" placeholder="correo@ejemplo.com"
              value={correo} onChange={(e) => setCorreo(e.target.value)}
            />
          </div>

          {/* Sexo */}
          <div className="col-md-3">
            <label htmlFor="sexo" className="form-label">Sexo</label>
            <select className="form-select" id="sexo" value={sexo} onChange={(e) => setSexo(e.target.value)}>
              <option value="" disabled>Selecciona</option>
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
          </div>

          {/* Tipo de Sangre */}
          <div className="col-md-3">
            <label htmlFor="tipoSangre" className="form-label">Tipo de Sangre</label>
            <select className="form-select" id="tipoSangre" value={tipoSangre} onChange={(e) => setTipoSangre(e.target.value)}>
              <option value="" disabled>Selecciona</option>
              <option value="A+">A+</option><option value="A-">A-</option>
              <option value="B+">B+</option><option value="B-">B-</option>
              <option value="AB+">AB+</option><option value="AB-">AB-</option>
              <option value="O+">O+</option><option value="O-">O-</option>
            </select>
          </div>

          {/* Botones */}
          <div className="d-flex justify-content-center gap-4 mt-4">
            <button className="btn btn-success px-4" disabled={enviando}>
              {enviando ? "Registrando..." : "Registrar"}
            </button>
            <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
             Salir
            </button>
            
          </div>
        </form>
      </div>
    </div>
  );
}
/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}